import { Salary } from './vacancy-data.component';
import { VacancyDetail, VacancySummary } from './vacancy-document';

/** Must stay byte-identical to the BFF's canonical slug (vacancy-documents/resolve). */
export function buildJobSlug(title: string | null | undefined): string {
  const safe = (title ?? '').trim();
  if (!safe) return 'vacature';
  const slug = safe
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'vacature';
}

export function jobPath(job: Pick<VacancySummary, 'id' | 'title'>): string {
  return `/vacatures/${buildJobSlug(job.title)}-${job.id}`;
}

const euro = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 });

/** Whole monthly euros. Values under 100 were entered in thousands ("5.5 - 7.45"). */
export function salaryRange(salary: Salary | null | undefined): { min: number; max: number } | null {
  const values = [salary?.min, salary?.max, salary?.static]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (values.length === 0) return null;
  const scale = values.every((value) => value < 100) ? 1000 : 1;
  const scaled = values.map((value) => Math.round(value * scale));
  return { min: Math.min(...scaled), max: Math.max(...scaled) };
}

export function formatEuro(value: number): string {
  return `€ ${euro.format(value)}`;
}

/** "€ 5.500 – € 7.450" (always gross per month on this site; the unit is shown separately). */
export function formatSalary(salary: Salary | null | undefined): string | null {
  const range = salaryRange(salary);
  if (!range) return null;
  if (range.min === range.max) return formatEuro(range.min);
  return `${formatEuro(range.min)} – ${formatEuro(range.max)}`;
}

function hourValues(hours: readonly string[] | null | undefined): number[] {
  return [...new Set((hours ?? []).map((hour) => Number.parseInt(String(hour), 10)).filter((hour) => Number.isFinite(hour) && hour > 0))].sort(
    (a, b) => a - b,
  );
}

/** "40 uur" or "32–40 uur". */
export function formatHours(hours: readonly string[] | null | undefined): string | null {
  const values = hourValues(hours);
  if (values.length === 0) return null;
  const min = values[0];
  const max = values[values.length - 1];
  return min === max ? `${min} uur` : `${min}–${max} uur`;
}

/** Display names people search for; the data keeps the official municipality names. */
const CITY_DISPLAY_NAMES: Record<string, string> = {
  "'s-gravenhage": 'Den Haag',
};

export function displayCity(city: string | null | undefined): string {
  const safe = (city ?? '').trim();
  return CITY_DISPLAY_NAMES[safe.toLowerCase().replace(/[’`]/g, "'")] ?? safe;
}

/** The client's sector ("ICT", "Overheid"); CATS reports a missing category as "Onbekend". */
export function displaySector(job: Pick<VacancySummary, 'industry'>): string | null {
  const sector = (job.industry ?? '').trim();
  return sector && sector.toLowerCase() !== 'onbekend' ? sector : null;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', euro: '€', ndash: '–', mdash: '—', hellip: '…' };

/** Plain text of a short HTML fragment (CATS summaries), without a DOM so it also runs on the server. */
export function htmlToText(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<(br|\/p|\/li|\/h[1-6])\b[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, name: string) => {
      if (name[0] === '#') {
        const code = name[1].toLowerCase() === 'x' ? Number.parseInt(name.slice(2), 16) : Number.parseInt(name.slice(1), 10);
        return Number.isFinite(code) ? String.fromCodePoint(code) : entity;
      }
      return ENTITIES[name.toLowerCase()] ?? entity;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

export function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Posted date of a job: the publication date, else the creation date. */
export function postedDate(job: Pick<VacancySummary, 'publicationDate' | 'dateCreated'>): Date | null {
  return toDate(job.publicationDate) ?? toDate(job.dateCreated);
}

const DAY_MS = 24 * 60 * 60 * 1000;
const longDate = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Amsterdam' });
const dayKey = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Amsterdam' });

/** Calendar days between two instants in Dutch local time. */
function calendarDaysBetween(earlier: Date, later: Date): number {
  const start = Date.parse(dayKey.format(earlier));
  const end = Date.parse(dayKey.format(later));
  return Math.round((end - start) / DAY_MS);
}

export function formatLongDate(date: Date | null): string {
  return date ? longDate.format(date) : '';
}

/** "vandaag", "gisteren", "5 dagen geleden", then the date itself after three weeks. */
export function formatPostedAgo(date: Date | null, now: Date = new Date()): string {
  if (!date) return '';
  const days = calendarDaysBetween(date, now);
  if (days <= 0) return 'vandaag';
  if (days === 1) return 'gisteren';
  if (days < 21) return `${days} dagen geleden`;
  return `op ${longDate.format(date)}`;
}

export function isoDate(date: Date | null): string | undefined {
  return date ? dayKey.format(date) : undefined;
}

/** schema.org employmentType values for a job's contract and weekly hours. */
export function schemaEmploymentTypes(job: Pick<VacancySummary, 'employment' | 'hours'>): string[] {
  const employment = (job.employment ?? '').toLowerCase();
  const types = new Set<string>();
  if (/freelance|zzp/.test(employment)) types.add('CONTRACTOR');
  if (/tijdelijk/.test(employment)) types.add('TEMPORARY');
  const hours = hourValues(job.hours);
  if (types.size === 0 || /vast/.test(employment)) {
    if (hours.length === 0 || hours[hours.length - 1] >= 36) types.add('FULL_TIME');
    if (hours.some((hour) => hour < 36)) types.add('PART_TIME');
  }
  return [...types];
}

export interface JobPostingInput {
  job: VacancyDetail;
  canonicalUrl: string;
  description: string;
  metaDescription: string;
}

/** WebPage + JobPosting graph for Google for Jobs. Only facts the site actually has are emitted. */
export function buildJobPostingGraph({ job, canonicalUrl, description, metaDescription }: JobPostingInput): object {
  const organization = { '@type': 'Organization', name: 'Blauwtand', url: 'https://blauwtand.com/' };
  const hiringOrganization = { ...organization, logo: 'https://blauwtand.com/assets/blauwtand.svg' };
  const posted = isoDate(postedDate(job));
  const modified = isoDate(toDate(job.dateModified)) ?? posted;
  const range = salaryRange(job.salary);
  const jobPostingId = `${canonicalUrl}#jobposting`;
  const jobPosting: Record<string, unknown> = {
    '@type': 'JobPosting',
    '@id': jobPostingId,
    title: job.title,
    description,
    identifier: { '@type': 'PropertyValue', name: 'Blauwtand', value: String(job.id) },
    datePosted: posted,
    employmentType: schemaEmploymentTypes(job),
    hiringOrganization,
    directApply: true,
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location?.city || undefined,
        addressRegion: job.location?.state || undefined,
        postalCode: job.location?.postalCode || undefined,
        addressCountry: 'NL',
      },
    },
    url: canonicalUrl,
    mainEntityOfPage: { '@id': canonicalUrl },
  };
  if (range) {
    jobPosting['baseSalary'] = {
      '@type': 'MonetaryAmount',
      currency: 'EUR',
      value:
        range.min === range.max
          ? { '@type': 'QuantitativeValue', value: range.min, unitText: 'MONTH' }
          : { '@type': 'QuantitativeValue', minValue: range.min, maxValue: range.max, unitText: 'MONTH' },
    };
  }
  const hours = hourValues(job.hours);
  if (hours.length > 0) jobPosting['workHours'] = formatHours(job.hours)?.replace('uur', 'uur per week');
  if (job.industries?.length) jobPosting['occupationalCategory'] = job.industries.join(', ');
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': canonicalUrl,
        url: canonicalUrl,
        name: job.title,
        description: metaDescription,
        inLanguage: 'nl-NL',
        datePublished: posted,
        dateModified: modified,
        author: organization,
        publisher: organization,
        mainEntity: { '@id': jobPostingId },
      },
      jobPosting,
    ],
  };
}
